import {X} from 'lucide-react';
import * as React from 'react';
import {CardArt} from 'src/CardArt';
import {
  CARD_PRODUCT_OPTIONS,
  cardProductLabel,
  type CardProductId,
  type CardProductOption,
} from 'src/cardProductOptions';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import {useSearchPopover} from 'src/SearchPopover/useSearchPopover';
import {useSearch} from 'src/useSearch';
import type {FocusControls} from 'src/FocusTrap';
import type {SubmitState} from 'src/SearchPopover/useSearchPopover';

// useSearch takes a mutable array, so hand it a copy rather than the frozen
// manifest export. (LogoSelect gets one incidentally, by filtering out its
// "None" entry; the card manifest has no such entry to filter.)
const OPTIONS: CardProductOption[] = [...CARD_PRODUCT_OPTIONS];

const RESULT_LIMIT = 40;

type Props = {
  /** Selected product slug; '' means none. */
  value: string;
  onChange: (product: CardProductId | '') => void;
  /** Omit to render bare, without the "Card face" InputGroup wrapper. */
  label?: string | null;
  /**
   * Accessible name for the input. Required when `label` is null, which
   * removes the only visible label this component owns.
   */
  ariaLabel?: string;
  autoFocus?: boolean;
  placeholder?: string;
};

/**
 * Searchable card-face picker, showing the face beside every option.
 *
 * This replaced a grid of every committed face. A grid reads well for a
 * handful of products, but the catalogue is heading for dozens, and at that
 * size it becomes a wall of thumbnails to hunt through with no way to filter.
 * Search covers both the label and the slug, the same as LogoSelect, which
 * this deliberately mirrors — same combobox wiring, same clear button, same
 * keyboard behaviour, so the two pickers in the account modal behave alike.
 *
 * Faces are far heavier than logos (hundreds of KB each), so mounting the
 * whole catalogue matters: the result cap below plus CardArt's lazy loading
 * keep an untouched picker from fetching every face on open.
 */
export const CardProductSelect = ({
  value,
  onChange,
  label = 'Card face',
  ariaLabel,
  autoFocus,
  placeholder = 'Search card faces…',
}: Props) => {
  const listboxId = React.useId();
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const activeItemRef = React.useRef<HTMLDivElement | null>(null);
  const anchorRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const focusControlsRef = React.useRef<FocusControls | null>(null);

  const {search} = useSearch(OPTIONS, ['label', 'value']);

  // useSearch returns every item verbatim for an empty query, ignoring the
  // Fuse limit, so cap it here rather than relying on that limit.
  const runSearch = (query: string): CardProductOption[] =>
    query.trim() === ''
      ? OPTIONS.slice(0, RESULT_LIMIT)
      : search(query, {limit: RESULT_LIMIT});

  const handleSubmit = (state: SubmitState<CardProductOption>) => {
    if (state.selection) onChange(state.selection.value);
    // Clear before closing, not after: handleQueryChange also sets isOpen, so
    // resetting the query last would immediately reopen the popover.
    handleQueryChange('');
    closePopover();
    if (state.focusDirection === 'backward') {
      focusControlsRef.current?.focusBackward();
    } else if (state.focusDirection === 'forward') {
      focusControlsRef.current?.focusForward();
    }
  };

  const handleClear = () => {
    onChange('');
    handleQueryChange('');
    closePopover();
  };

  const {
    isOpen,
    query,
    searchResults,
    selectedIndex,
    handleKeyDown,
    handleQueryChange,
    openPopover,
    closePopover,
  } = useSearchPopover<CardProductOption>({
    initialQuery: '',
    focusControlsRef,
    search: runSearch,
    onSubmit: handleSubmit,
  });

  React.useLayoutEffect(() => {
    if (isOpen && anchorRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${anchorRef.current.offsetWidth}px`;
    }
  }, [anchorRef.current, popoverRef.current, isOpen]);

  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  const picker = (
    <div className="flex items-center gap-2">
      <CardArt product={value} className="w-12" alt={value} />
      <div className="relative flex-1">
        <TextInput
          ref={anchorRef}
          value={query}
          onChange={handleQueryChange}
          onClick={openPopover}
          onFocus={openPopover}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          selectOnFocus
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          // Combobox wiring, as in LogoSelect: a bare text input plus styled
          // divs gives assistive tech nothing, so arrow keys would only change
          // colours as far as a screen reader is concerned.
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && selectedIndex !== null ? optionId(selectedIndex) : undefined
          }
          placeholder={cardProductLabel(value) ?? placeholder}
        />
      </div>
      {value !== '' && (
        <button
          type="button"
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground shrink-0 p-1"
          aria-label={ariaLabel ? `Clear ${ariaLabel}` : 'Clear card face'}
        >
          <X className="size-4" />
        </button>
      )}
      <Popover
        anchorRef={anchorRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={isOpen}
        onOutsideClick={closePopover}
        onOutsideFocus={closePopover}
        passThroughOutsideClicks
      >
        <PopoverContents ref={popoverRef} id={listboxId} role="listbox">
          {searchResults.length === 0 ? (
            <div className="text-muted-foreground px-3 py-2">
              No card faces match
            </div>
          ) : (
            searchResults.map((option, index) => (
              <PopoverItem
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={selectedIndex === index}
                isSelected={selectedIndex === index}
                ref={selectedIndex === index ? activeItemRef : null}
                onClick={() => handleSubmit({query, selection: option})}
              >
                <div className="flex items-center gap-2.5">
                  <CardArt product={option.value} className="w-10" />
                  <span className="truncate">{option.label}</span>
                </div>
              </PopoverItem>
            ))
          )}
        </PopoverContents>
      </Popover>
    </div>
  );

  return label === null ? picker : <InputGroup label={label}>{picker}</InputGroup>;
};
